import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import FlagBadge from "@/components/localization/FlagBadge";
import { getSamePageLocalizedPath, languageLabels } from "@/lib/i18nRoutes";
import { useLanguage } from "@/lib/LanguageContext";

const languageOrder = ["en", "he", "es", "it", "fr", "de", "pt", "ar", "el", "ru", "tr", "zh"];
const languages = languageOrder.map((code) => ({
  code,
  name: languageLabels[code]?.name || code,
}));

function FlagImage({ item, className = "h-5 w-7" }) {
  return <FlagBadge code={item.code} className={className} />;
}

const chooseLanguageLabels = {
  en: "Choose language",
  he: "בחרו שפה",
  es: "Elegir idioma",
  it: "Scegli lingua",
  fr: "Choisir la langue",
  de: "Sprache wählen",
  pt: "Escolher idioma",
  ar: "اختر اللغة",
  el: "Επιλογή γλώσσας",
  ru: "Выбрать язык",
  tr: "Dil seç",
  zh: "选择语言",
};

function searchWithoutLang(search = "") {
  const params = new URLSearchParams(search);
  params.delete("lang");
  const value = params.toString();
  return value ? `?${value}` : "";
}

export default function PublicLanguageSwitcher() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setUserLanguage } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const switcherRef = React.useRef(null);
  const lang = params.lang || language || "en";
  const chooseLanguageLabel = chooseLanguageLabels[lang] || "";

  React.useEffect(() => {
    const close = (event) => {
      if (!switcherRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const changeLanguage = (nextLang) => {
    setOpen(false);
    if (nextLang === lang) return;
    setUserLanguage(nextLang);

    const nextPath = getSamePageLocalizedPath(location.pathname, nextLang);
    const cleanSearch = searchWithoutLang(location.search);

    navigate(`${nextPath}${cleanSearch}${location.hash || ""}`, { replace: true });
    window.dispatchEvent(new CustomEvent("marstick:language-route-updated", { detail: { language: nextLang, path: nextPath } }));
  };

  const currentLanguage = languages.find((item) => item.code === lang) || languages[0];

  return (
    <div ref={switcherRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="group inline-flex h-12 min-w-[3.5rem] items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-slate-950 shadow-sm backdrop-blur-xl transition-colors duration-200 hover:border-primary/45 hover:bg-primary/10 hover:text-primary dark:border-white/15 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 md:h-11 md:min-w-[4.25rem] md:gap-2 md:px-3"
        aria-label={chooseLanguageLabel}
        aria-expanded={open}
        title={`${chooseLanguageLabel}: ${currentLanguage.name}`}
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 transition group-hover:bg-primary/15 dark:bg-cyan-400/10" aria-hidden="true">
          <FlagImage item={currentLanguage} className="h-4 w-6" />
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[120] w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 text-slate-950 shadow-2xl shadow-ocean/20 ring-1 ring-black/5 dark:border-white/15 dark:bg-slate-950 dark:text-white dark:ring-white/10">
          {languages.map((item) => (
            <button
              key={item.code}
              type="button"
              onClick={() => changeLanguage(item.code)}
              className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-bold hover:bg-primary/10 hover:text-primary dark:hover:bg-white/10 dark:hover:text-cyan-100"
            >
              <span className="inline-flex items-center gap-3"><FlagImage item={item} /><span>{item.name}</span></span>
              {item.code === lang && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}